import { Button, Icon, Input, Modal, AddClientForm } from '@components';
import { Client } from '@utilities/types';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ColorsEnum } from '@utilities/enums';

interface Props {
    suggestions: Client[];
    placeholder?: string;
    selectedClient: Client | null;
    onFindClient: (search: string) => void;
    onSelect: (suggestion: Client) => void;
    onCreateClient: (client: Partial<Client>) => void
    onClearClient: () => void;
}

export const SearchClient: FC<Props> = ({
    suggestions,
    selectedClient,
    placeholder = "Rechercher un client...",
    onFindClient,
    onSelect,
    onCreateClient,
    onClearClient,
}) => {

    const [query, setQuery] = useState<string>('');
    const [addClientModal, setAddClientModal] = useState<boolean>(false);

    const filteredSuggestions = useMemo(() => {
        const lowerQuery = query.toLowerCase();
        return suggestions.filter((s) =>
            `${s.firstname} ${s.lastname}`.toLowerCase().includes(lowerQuery) ||
            s.phoneNumber!.includes(lowerQuery) ||
            s.email!.toLowerCase().includes(lowerQuery)
        );
    }, [query, suggestions]);

    useEffect(()=> {
        if(query.length > 0) {
            onFindClient(query);
        }
    }, [query]);

    const handleCreateClient = useCallback((client: Partial<Client>) => {
        onCreateClient(client);
        setAddClientModal(false);
    }, [onCreateClient]);

    return (
        <div className="flex-1 relative">
            <Input
                className="input input-bordered w-full"
                placeholder={placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />

            {query && filteredSuggestions.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 shadow-md rounded-md max-h-60 overflow-y-auto">
                {filteredSuggestions.map((s) => (
                    <li
                        key={s._id}
                        onClick={() => {
                            onSelect(s);
                            setQuery('');
                        }}
                        className="p-3 cursor-pointer hover:bg-gray-100 border-b"
                    >
                        <p className="font-medium">
                            {s.firstname} {s.lastname}
                        </p>
                        <p className="text-sm text-gray-500">{s.phoneNumber}</p>
                        <p className="text-sm text-gray-400">{s.email}</p>
                    </li>
                ))}
                </ul>
            )}

            {
                selectedClient && (
                    <div className='mt-4'>
                        <p className="text-sm font-medium">
                            Client sélectionné :
                        </p>
                        <div className="flex flex-col gap-1 p-3 border border-gray-200 rounded-md bg-gray-50 mt-2 relative">
                            <p className="text-lg font-semibold">
                                {selectedClient.firstname} {selectedClient.lastname}
                            </p>
                            <p>
                                {selectedClient.phoneNumber}
                            </p>
                            <p>
                                {selectedClient.email}
                            </p>

                            <Button 
                                variant="secondary"
                                className='rounded-btn btn-circle absolute top-2 right-2 btn-sm'
                                onClick={onClearClient}
                            >
                                <Icon 
                                    iconName="Trash2"
                                    color={ColorsEnum.SECONDARY_CONTENT}
                                    size={20}
                                />
                            </Button>
                        </div>
                    </div>
                )
            }

            {
                !selectedClient && (
                    <div className='mt-4'>
                        <Button
                            className='rounded-btn'
                            onClick={()=> setAddClientModal(true)}
                        >
                            Ajouter un client
                        </Button>
                    </div>
                )
            }

            <Modal
                isOpen={addClientModal}
                onClose={() => setAddClientModal(false)}
                title="Ajouter un client"
            >
                <AddClientForm 
                    onCreateClient={(client)=> handleCreateClient(client)}
                    onCancel={()=> setAddClientModal(false)}
                />
            </Modal>
        </div>
    );
};
